import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, HostListener, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, finalize } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { ShipmentManagementService, ClientsBrands } from '../../../services/shipment-management.service';
import { NotificationService } from '../../../services/notification.service';
import { Waybill } from '../../../interfaces/Waybills.interfaces';
import { SearchableSelectComponent, SearchableSelectOption } from '../../../components/searchable-select/searchable-select.component';

export interface TourSummaryClient {
  clientCode: string;
  clientName: string;
  totalWaybills: number;
  totalParcels: number;
  totalReWaybills: number;
  checked: boolean;
}

export interface TourSummary {
  tourName: string;
  clients: TourSummaryClient[];
}

export interface SelectedTour {
  tour: string;
  selectionOrder: number;
}

@Component({
  selector: 'app-tours-print-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SearchableSelectComponent],
  templateUrl: './tours-print-popup.component.html',
  styleUrls: ['./tours-print-popup.component.css']
})
export class ToursPrintPopupComponent implements OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Input() waybills: Waybill[] = [];
  @Input() clientsBrands: ClientsBrands[] = [];
  /** Tutti i tour disponibili (codici instradamento), indipendenti dalla pagina corrente */
  @Input() allTours: string[] = [];

  @Output() closed = new EventEmitter<void>();
  @Output() printed = new EventEmitter<{ tour: string; date: string }>();

  @ViewChild('tourDateInput', { static: false }) tourDateInput!: ElementRef<HTMLInputElement>;

  selectedToursForPrint: string[] = [];
  /** Data selezionata per la stampa */
  selectedTourDate: Date | null = null;
  private tourDateFlatpickrInstance: FlatpickrInstance | null = null;
  isPrintingTour: boolean = false;
  isDropdownOpen: boolean = false;
  tourSearchText: string = '';

  // Tour summary state
  tourSummaries: TourSummary[] = [];
  isLoadingSummary: boolean = false;
  printReWaybills: boolean = true;
  private summarySubscription: Subscription | null = null;
  private tourSelectionCounter: number = 0;

  /**
   * Get selected tours with their selection order
   */
  get selectedToursWithOrder(): SelectedTour[] {
    return this.selectedToursForPrint.map((tour, index) => ({
      tour,
      selectionOrder: index + 1
    })).sort((a, b) => a.selectionOrder - b.selectionOrder);
  }

  /**
   * Opzioni per il select ricercabile dei tour (con salvataggio delle selezioni).
   * Campo memorizzato, non getter: un array nuovo a ogni change detection farebbe
   * ricreare le opzioni nel dropdown e i click non arriverebbero al gestore.
   */
  tourSelectOptions: SearchableSelectOption[] = [];

  private refreshTourSelectOptions(): void {
    this.tourSelectOptions = this.availableTours.map(tour => ({value: tour, label: tour}));
  }

  /**
   * Cambio selezione dal select ricercabile: mantiene l'ordine di selezione
   * (l'ordine dei valori è quello di click, o quello della selezione salvata applicata)
   */
  onToursSelectChange(values: string | string[]): void {
    if (this.isPrintingTour) return;
    const selected = Array.isArray(values) ? [...values] : (values ? [values] : []);
    this.selectedToursForPrint = selected;
    if (selected.length === 0) {
      this.clearTourDate();
      this.tourSummaries = [];
      this.isLoadingSummary = false;
      this.cdr.detectChanges();
      return;
    }
    this.onTourSelectionChange();
  }

  // ==================== SELEZIONE CLIENTI (dai dati del riepilogo backend) ====================

  /** Opzioni del select clienti: clienti distinti tra tutti i tour caricati */
  clientSelectOptions: SearchableSelectOption[] = [];
  /** Codici cliente selezionati (= clienti spuntati in almeno un tour) */
  selectedClientCodes: string[] = [];

  /** Ricostruisce le opzioni clienti dopo il caricamento del riepilogo (tutti selezionati) */
  private refreshClientSelectOptions(): void {
    const byCode = new Map<string, string>();
    for (const client of this.allTourClients) {
      if (client.clientCode && !byCode.has(client.clientCode)) {
        byCode.set(client.clientCode, client.clientName || '');
      }
    }
    this.clientSelectOptions = Array.from(byCode.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([code, name]) => ({value: code, label: name ? `${code} - ${name}` : code}));
    this.syncSelectedClientCodesFromChecks();
  }

  /** Cambio dal select clienti: applica la spunta ai clienti di tutti i tour */
  onClientsSelectChange(values: string | string[]): void {
    if (this.isPrintingTour) return;
    const codes = Array.isArray(values) ? [...values] : (values ? [values] : []);
    this.selectedClientCodes = codes;
    for (const client of this.allTourClients) {
      client.checked = codes.includes(client.clientCode);
    }
  }

  /** Cambio di una spunta nella lista per tour: riallinea il select */
  onClientCheckboxChange(): void {
    this.syncSelectedClientCodesFromChecks();
  }

  private syncSelectedClientCodesFromChecks(): void {
    const checked = new Set(this.allTourClients.filter(c => c.checked).map(c => c.clientCode));
    this.selectedClientCodes = this.clientSelectOptions
      .map(o => o.value)
      .filter(code => checked.has(code));
  }

  /**
   * Get available tours filtered by search text
   */
  get filteredTours(): string[] {
    if (!this.tourSearchText || this.tourSearchText.trim() === '') {
      return this.availableTours;
    }
    const search = this.tourSearchText.toLowerCase().trim();
    return this.availableTours.filter(t => t.toLowerCase().includes(search));
  }

  /**
   * Get all clients across all tours (flat list)
   */
  get allTourClients(): TourSummaryClient[] {
    return this.tourSummaries.flatMap(t => t.clients);
  }

  /**
   * Get total waybills from checked clients across all tours
   */
  get totalWaybills(): number {
    return this.allTourClients
      .filter(c => c.checked)
      .reduce((sum, c) => sum + c.totalWaybills, 0);
  }

  /**
   * Get total parcels from checked clients across all tours
   */
  get totalParcels(): number {
    return this.allTourClients
      .filter(c => c.checked)
      .reduce((sum, c) => sum + c.totalParcels, 0);
  }

  /**
   * Get total re-waybills from checked clients across all tours
   */
  get totalReWaybills(): number {
    return this.allTourClients
      .filter(c => c.checked)
      .reduce((sum, c) => sum + c.totalReWaybills, 0);
  }

  constructor(
    private shipmentManagementService: ShipmentManagementService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) {}

  @HostListener('document:mousedown', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    // Don't close dropdown if clicking on a chip-close
    if (target.closest('.chip-close')) return;
    const selectWrapper = this.elementRef.nativeElement.querySelector('.tour-select-wrapper');
    if (selectWrapper && !selectWrapper.contains(target)) {
      this.isDropdownOpen = false;
      this.tourSearchText = '';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allTours'] || changes['waybills'] || changes['visible']) {
      this.refreshTourSelectOptions();
    }
    if (changes['visible']) {
      if (changes['visible'].currentValue === true) {
        this.resetForm();
      } else if (changes['visible'].currentValue === false && changes['visible'].previousValue === true) {
        this.resetForm();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.tourDateFlatpickrInstance) {
      this.tourDateFlatpickrInstance.destroy();
      this.tourDateFlatpickrInstance = null;
    }
  }

  /**
   * Tutti i tour disponibili: usa la lista completa fornita dal padre,
   * con fallback sui delivery type delle bolle caricate.
   */
  get availableTours(): string[] {
    if (this.allTours && this.allTours.length > 0) {
      return [...this.allTours].sort();
    }
    const uniqueDeliveryTypes = new Set(
      this.waybills
        .map(w => w.waybillDeliveryType)
        .filter((type): type is string => type !== undefined && type !== null && type.trim() !== '')
    );
    return Array.from(uniqueDeliveryTypes).sort();
  }

  /**
   * Data selezionata convertita nel formato dd/MM/yyyy richiesto dalla stampa
   */
  get selectedTourDateForPrint(): string {
    return this.selectedTourDate ? this.formatDateToDDMMYYYY(this.selectedTourDate) : '';
  }

  /**
   * Data formattata dd/MM/yyyy per la visualizzazione nell'input
   */
  getFormattedTourDate(): string {
    return this.selectedTourDateForPrint;
  }

  /**
   * Handle tour selection change - manage multiple selections
   */
  onTourSelectionChange(event?: Event): void {
    // Pre-seleziona la data odierna alla prima selezione di un tour
    if (this.selectedToursForPrint.length > 0 && !this.selectedTourDate) {
      this.setTourDate(this.startOfToday());
      return; // setTourDate ricarica già il riepilogo
    }
    this.loadTourSummary();
  }

  /**
   * Inizializza Flatpickr sull'input della data tour
   */
  private initializeTourDateFlatpickr(): void {
    if (this.tourDateInput?.nativeElement) {
      if (this.tourDateFlatpickrInstance) {
        this.tourDateFlatpickrInstance.destroy();
      }

      this.tourDateFlatpickrInstance = flatpickr(this.tourDateInput.nativeElement, {
        dateFormat: 'd/m/Y',
        allowInput: false,
        clickOpens: false,
        locale: Italian,
        defaultDate: this.selectedTourDate || undefined,
        onChange: (selectedDates: Date[]) => {
          if (selectedDates.length > 0) {
            const selectedDate = new Date(selectedDates[0]);
            selectedDate.setHours(0, 0, 0, 0);
            this.selectedTourDate = selectedDate;
            this.tourDateFlatpickrInstance?.close();
            this.onDateSelectionChange();
          }
        },
        onClose: () => {
          if (this.tourDateInput?.nativeElement) {
            this.tourDateInput.nativeElement.blur();
          }
        }
      });
    }
  }

  /**
   * Apre il calendario flatpickr della data tour
   */
  openTourDatePicker(): void {
    if (this.isPrintingTour || this.selectedToursForPrint.length === 0) return;

    if (!this.tourDateFlatpickrInstance) {
      this.initializeTourDateFlatpickr();
    }

    if (this.tourDateFlatpickrInstance && !this.tourDateFlatpickrInstance.isOpen) {
      if (this.selectedTourDate) {
        this.tourDateFlatpickrInstance.setDate(this.selectedTourDate);
      }
      this.tourDateFlatpickrInstance.open();
    }
  }

  /** Giorno precedente */
  decrementTourDate(): void {
    if (this.isPrintingTour || this.selectedToursForPrint.length === 0) return;
    const base = this.selectedTourDate || this.startOfToday();
    const newDate = new Date(base);
    newDate.setDate(newDate.getDate() - 1);
    newDate.setHours(0, 0, 0, 0);
    this.setTourDate(newDate);
  }

  /** Giorno successivo */
  incrementTourDate(): void {
    if (this.isPrintingTour || this.selectedToursForPrint.length === 0) return;
    const base = this.selectedTourDate || this.startOfToday();
    const newDate = new Date(base);
    newDate.setDate(newDate.getDate() + 1);
    newDate.setHours(0, 0, 0, 0);
    this.setTourDate(newDate);
  }

  /** Imposta la data a oggi */
  setTourDateToday(): void {
    if (this.isPrintingTour || this.selectedToursForPrint.length === 0) return;
    this.setTourDate(this.startOfToday());
  }

  /** Cancella la data */
  clearTourDate(): void {
    if (this.isPrintingTour) return;
    this.selectedTourDate = null;
    this.tourDateFlatpickrInstance?.clear();
  }

  /** Imposta la data, sincronizza flatpickr e ricarica il riepilogo */
  private setTourDate(date: Date): void {
    this.selectedTourDate = date;
    if (this.tourDateFlatpickrInstance) {
      this.tourDateFlatpickrInstance.setDate(date);
    }
    this.onDateSelectionChange();
  }

  private startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  /**
   * Toggle tour dropdown
   */
  toggleTourDropdown(): void {
    if (!this.isPrintingTour) {
      this.isDropdownOpen = !this.isDropdownOpen;
      if (!this.isDropdownOpen) {
        this.tourSearchText = '';
      }
    }
  }

  /**
   * Toggle tour selection (add / remove from selection)
   */
  toggleTourSelection(tour: string): void {
    if (this.isPrintingTour) return;
    
    const index = this.selectedToursForPrint.indexOf(tour);
    if (index > -1) {
      // Remove
      this.selectedToursForPrint.splice(index, 1);
      if (this.selectedToursForPrint.length === 0) {
        this.clearTourDate();
      }
    } else {
      // Add
      this.selectedToursForPrint.push(tour);
    }
    
    this.onTourSelectionChange();
  }

  /**
   * Get the selection order for a tour
   */
  getSelectionOrder(tour: string): number {
    return this.selectedToursForPrint.indexOf(tour) + 1;
  }

  /**
   * Remove a tour from selection
   */
  removeTourFromSelection(tourToRemove: string): void {
    this.selectedToursForPrint = this.selectedToursForPrint.filter(t => t !== tourToRemove);

    if (this.selectedToursForPrint.length === 0) {
      this.clearTourDate();
      this.tourSummaries = [];
      this.isLoadingSummary = false;
    } else {
      this.loadTourSummary();
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle date selection change - reload summary
   */
  onDateSelectionChange(): void {
    this.loadTourSummary();
  }

  /**
   * Load tour summary from backend for all selected tours
   */
  private loadTourSummary(): void {
    // Cancel previous request
    if (this.summarySubscription) {
      this.summarySubscription.unsubscribe();
      this.summarySubscription = null;
    }

    if (this.selectedToursForPrint.length === 0) {
      this.tourSummaries = [];
      this.isLoadingSummary = false;
      return;
    }

    const dateToUse = this.selectedTourDateForPrint || this.formatDateToDDMMYYYY(new Date());

    this.isLoadingSummary = true;
    this.tourSummaries = [];
    this.clientSelectOptions = [];
    this.selectedClientCodes = [];

    this.summarySubscription = this.shipmentManagementService.getTourSummary(
      this.selectedToursForPrint,
      dateToUse,
      this.clientsBrands
    ).pipe(
      catchError(error => {
        console.error('Error loading tour summary:', error);
        this.notificationService.error('Errore durante il caricamento del riepilogo tour');
        return of(null);
      }),
      finalize(() => {
        this.isLoadingSummary = false;
      })
    ).subscribe({
      next: (response) => {
        if (response && response.tours) {
          this.tourSummaries = response.tours.map(tour => ({
            tourName: tour.tourName,
            clients: tour.clients.map(c => ({
              ...c,
              checked: true
            }))
          }));
          this.refreshClientSelectOptions();
        }
      }
    });
  }

  /**
   * Execute tour printing
   */
  executePrintTour(): void {
    if (this.selectedToursForPrint.length === 0) {
      this.notificationService.warning('Seleziona almeno un tour da stampare');
      return;
    }

    if (!this.selectedTourDateForPrint) {
      this.notificationService.warning('Seleziona una data per i tour');
      return;
    }

    this.isPrintingTour = true;

    // Build tours array with per-tour checked clients
    const toursPayload = this.tourSummaries
      .filter(ts => this.selectedToursForPrint.includes(ts.tourName))
      .map(ts => ({
        tourName: ts.tourName,
        clients: ts.clients
          .filter(c => c.checked)
          .map(c => ({
            clientCode: c.clientCode,
            isRewaybill: this.printReWaybills
          }))
      }));

    this.shipmentManagementService.printTour(
      toursPayload,
      this.selectedTourDateForPrint,
      this.clientsBrands
    ).pipe(
      catchError(error => {
        console.error('Error printing tour:', error);
        this.notificationService.error('Errore durante la stampa del tour');
        return of(null);
      }),
      finalize(() => {
        this.isPrintingTour = false;
      })
    ).subscribe({
      next: (response) => {
        if (response) {
          const toursList = this.selectedToursForPrint.join(', ');
          this.notificationService.success(
            `Tour [${toursList}] per il ${this.selectedTourDateForPrint} stampato con successo`
          );
          this.printed.emit({
            tour: this.selectedToursForPrint.join(', '),
            date: this.selectedTourDateForPrint
          });
          this.close();
        }
      },
      error: (error) => {
        console.error('Error in tour printing subscription:', error);
        this.notificationService.error('Errore durante la stampa del tour');
      }
    });
  }

  /**
   * Close the popup
   */
  close(): void {
    if (this.summarySubscription) {
      this.summarySubscription.unsubscribe();
      this.summarySubscription = null;
    }
    this.isDropdownOpen = false;
    this.resetForm();
    this.closed.emit();
  }

  /**
   * Reset form to initial state
   */
  private resetForm(): void {
    this.selectedToursForPrint = [];
    this.selectedTourDate = null;
    this.tourDateFlatpickrInstance?.clear();
    this.isPrintingTour = false;
    this.tourSummaries = [];
    this.isLoadingSummary = false;
    this.printReWaybills = true;
    this.tourSelectionCounter = 0;
    this.isDropdownOpen = false;
    this.clientSelectOptions = [];
    this.selectedClientCodes = [];
  }

  /**
   * Formatta una data nel formato dd/mm/yyyy
   */
  private formatDateToDDMMYYYY(dateValue: any): string {
    if (!dateValue) return '';

    try {
      let date: Date;

      if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
        return dateValue;
      }

      if (typeof dateValue === 'string') {
        if (dateValue.includes('-')) {
          date = new Date(dateValue);
        } else {
          date = new Date(dateValue);
        }
      } else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) {
        return String(dateValue);
      }

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.warn('Error formatting date:', dateValue, error);
      return String(dateValue);
    }
  }
}
